import type { SafeString } from '@ember/template';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { eq } from 'ember-truth-helpers';

import type { BarData } from '../../../types/components/prism/data-bar.ts';
import {
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableValue,
} from '../../../utils/css-variable.ts';

type ProgressValues = {
  isGradient?: boolean;
  isStriped?: boolean;
  style: SafeString;
};

interface PrismDataBarDataBarBasicSignature {
  Args: {
    backgroundColor?: string;
    data?: BarData[];
    dataBarThresholdStyle?: SafeString;
    direction?: 'regular' | 'reverse';
    isGradient?: boolean;
    isStriped?: boolean;
    rounded?: 'left' | 'right' | 'both';
  };
  Element: HTMLDivElement;
}

export default class PrismDataBarDataBarBasic extends Component<PrismDataBarDataBarBasicSignature> {
  // regular (default) or reverse
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get direction() {
    return this.args.direction ?? 'regular';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get progressContainerBackgroundColor() {
    if (!this.args.backgroundColor) {
      return '';
    }
    return htmlSafe(`background-color: ${this.args.backgroundColor};`);
  }

  get progressValues(): ProgressValues[] {
    if (!this.args.data) {
      return [
        {
          style: htmlSafe(
            `width: 0%;background-color: ${getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL)};`,
          ),
          isStriped: undefined,
          isGradient: undefined,
        },
      ];
    }

    return this.args.data
      .filter(
        (dataBar) => (dataBar?.widthPercentage ?? 0) !== 0, // Do not render progress bars with 0 width, if it has a border it will be visible
      )
      .map((dataBar) => {
        const isStriped = dataBar.isStriped || this.args.isStriped;
        const isGradient = dataBar.isGradient || this.args.isGradient;

        if (isStriped || isGradient) {
          return {
            style: htmlSafe(`width: ${dataBar?.widthPercentage ?? 0}%`),
            isStriped,
            isGradient,
          };
        }

        return {
          style: htmlSafe(
            `width: ${dataBar?.widthPercentage ?? 0}%;background-color: ${
              dataBar?.backgroundColor ??
              getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL)
            };`,
          ),
        };
      });
  }

  <template>
    <div
      class="prism-data-bar-basic
        {{if (eq @rounded 'left') 'prism-data-bar-basic--left'}}
        {{if (eq @rounded 'right') 'prism-data-bar-basic--right'}}"
      style={{this.progressContainerBackgroundColor}}
      ...attributes
    >
      <div class="data-bar-container" style={{@dataBarThresholdStyle}}>
        {{#each this.progressValues as |progressValue|}}
          <div
            class="data-bar data-bar-{{this.direction}}
              {{if progressValue.isStriped 'data-bar--striped'}}
              {{if progressValue.isGradient 'data-bar--gradient'}}"
            role="progressbar"
            style={{progressValue.style}}
          ></div>
        {{/each}}
      </div>
    </div>
  </template>
}
